import { createContext, useContext } from "react";

export type AuthStatus = "loading" | "signed_in" | "signed_out";

/**
 * The steps a sign-in can stop at. All three are consequences of how the
 * admin pool is set up rather than choices this screen makes:
 * `password_reset_required` because accounts are admin-created and land on
 * a temporary password, and the two TOTP steps because the pool asks for a
 * second factor.
 */
export type SignInStep =
  | "idle"
  | "totp_required"
  | "totp_setup_required"
  | "password_reset_required";

export interface AuthUser {
  username: string;
  email: string;
}

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
  step: SignInStep;
  /** True when this session is the local header bypass rather than a real
   *  token, so the shell can say so instead of implying otherwise. */
  isDevSession: boolean;
  signIn(email: string, password: string): Promise<void>;
  confirmTotp(code: string): Promise<void>;
  completeNewPassword(newPassword: string): Promise<void>;
  signOut(): Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth() called outside <AuthProvider>");
  return value;
}
